import { type ReactNode, useEffect, useState } from "react";
import { externalSession } from "../../external/web/session.ts";
import { tenantPath, tenantPortalUrl } from "../tenant-paths.ts";
import { type PortalEntry, portalEntry } from "./portal-entry.ts";
import { RouteContent } from "./RouteContent.tsx";
import { buttonClass, ErrorText, Feedback } from "./ui/index.ts";

export const RootInfoView = ({ entry, origin }: { entry: PortalEntry; origin: string }): ReactNode => {
  if (entry.site === "public") {
    return (
      <section className="stack">
        <h1 className="t-display">Use your organisation’s sign-in link</h1>
        <p className="t-body measure">Use the sign-in link from your organisation to open the portal.</p>
      </section>
    );
  }
  const address = tenantPortalUrl(origin, entry.internalSlug);
  return (
    <section className="stack">
      <h1 className="t-display">This address has moved</h1>
      <p className="t-body measure">
        Use <strong>{address}</strong> to open the portal, and update your bookmarks.
      </p>
      <div>
        <a href={tenantPath(entry.internalSlug)} className={buttonClass("primary")}>
          Go
        </a>
      </div>
    </section>
  );
};

type RootState = { status: "loading" } | { status: "error" } | { status: "ready"; entry: PortalEntry };

// A signed-in visitor goes straight to their portal; anyone else learns which address to use.
const resolveRoot = async (signal: AbortSignal): Promise<RootState | { status: "redirect"; path: string }> => {
  const session = await externalSession(signal).catch(() => null);
  if (session) return { status: "redirect", path: tenantPath(session.tenantSlug) };
  return portalEntry(signal).then(
    (entry): RootState => ({ status: "ready", entry }),
    (): RootState => ({ status: "error" }),
  );
};

export const RootInfo = (): ReactNode => {
  const [state, setState] = useState<RootState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    void resolveRoot(controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      if (result.status === "redirect") window.location.replace(result.path);
      else setState(result);
    });
    return () => controller.abort();
  }, []);

  return (
    <div className="app-main">
      <RouteContent pathname="/">
        {state.status === "loading" && <Feedback>Loading…</Feedback>}
        {state.status === "error" && <ErrorText>Could not load the portal. Try again later.</ErrorText>}
        {state.status === "ready" && <RootInfoView entry={state.entry} origin={window.location.origin} />}
      </RouteContent>
    </div>
  );
};
